<template>
  <div class="faq">
    <TheHeader :scrollY="0" />
    <div class="container">
      <p class="tag">FAQs</p>
      <h1>We've already answered</h1>
      <div class="info">
        <div class="col">
          <div class="info__block">
            <h2>Get Started</h2>
            <Accordion>
              <template #question>
                <p>What is PatternFast?</p>
              </template>
              <template #answer>
                <p>PatternFast is a professional-grade fashion design and development platform that empowers fashion brands, manufacturers, indie designers, and schools to create instantly manufacturable, standard-sized and custom-fit designs.</p><p>It combines AI-driven tools, rapid pattern drafting, automatic grading, and tech pack generation—all in one seamless web-based system.</p>
              </template>
            </Accordion>
            <Accordion>
              <template #question>
                <p>Who is PatternFast for?</p>
              </template>
              <template #answer>
                <p>
                  PatternFast is built for professionals across the fashion industry:</p><p>Manufacturers needing fast, accurate pattern blocks and tech packs;</p><p>Fashion brands designing custom or seasonal collections;</p><p>Boutiques looking to offer exclusive, branded apparel;</p><p>Indie designers who need constantly new designs without hiring a team;</p><p>Apparel merchandisers planning and building digital collections;</p><p>Fashion schools preparing students with industry-grade tech.
                </p>
              </template>
            </Accordion>
            <Accordion>
              <template #question>
                <p>Do I need advanced pattern-making skills to use PatternFast?</p>
              </template>
              <template #answer>
                <p>
                  No, PatternFast automates many complex processes like pattern drafting, grading, fit adjustments, and tech pack creation.</p><p>However, a foundational understanding of garment construction and apparel development is recommended for best results.
                </p>
              </template>
            </Accordion>
            <Accordion>
              <template #question>
                <p>Can I use PatternFast if I’m an independent designer or student?</p>
              </template>
              <template #answer>
                <p>
                  Absolutely.</p><p>While PatternFast was built with professionals and enterprises in mind, we offer pricing plans specifically for individuals and students who want access to our tools without the overhead.
                </p>
              </template>
            </Accordion>
            <Accordion>
              <template #question>
                <p>Does PatternFast support sustainable fashion practices?</p>
              </template>
              <template #answer>
                <p>
                  Absolutely.</p><p>Our digital-first approach minimizes physical sampling and fabric waste.</p><p>Our pattern and grading tools reduce costly errors, supporting an eco-friendly, made-to-order fashion model, as well as rapid mass pre-production.
                </p>
              </template>
            </Accordion>
            <Accordion>
              <template #question>
                <p>How is PatternFast different from other design software?</p>
              </template>
              <template #answer>
                <p>
                  Unlike most 3D software that focus on visual rendering, PatternFast is built to generate instantly manufacturable designs, which include a ready-for-production pattern, automated graded size run, tech packs and other critical information.</p><p>Our AI tools prioritize precision, speed, and production-readiness, making it ideal for professionals bringing real products to market.
                </p>
              </template>
            </Accordion>
            <Accordion>
              <template #question>
                <p>How can I request a demo?</p>
              </template>
              <template #answer>
                <p>
                  You can request a free demo via our homepage or directly contact us at info@patternfast.com.</p><p>We’ll walk you through the platform and help tailor it to your needs.
                </p>
              </template>
            </Accordion>
            <Accordion>
              <template #question>
                <p>How do I start a new design on PatternFast?</p>
              </template>
              <template #answer>
                <p>
                  Login, click on your user icon and start creating your first collection.</p><p>Once you've created your first collection, you can create your first design.</p><p>Push the 'Create Design' button and go through the flow to start designing.</p><p>Don't forget to save your design at the end of the process.
                </p>
              </template>
            </Accordion>
          </div>
          <div class="info__block">
            <h2 style="margin-top: 40px;">Integrations</h2>
            <Accordion>
              <template #question>
                <p>Is PatternFast cloud-based?</p>
              </template>
              <template #answer>
                <p>
                  Yes, PatternFast is entirely web-based.</p><p>No installation required—just log in through your browser and start designing.</p><p>All updates and new features are delivered automatically.
                </p>
              </template>
            </Accordion>
            <Accordion>
              <template #question>
                <p>Is PatternFast compatible with other industry tools?</p>
              </template>
              <template #answer>
                <p>
                  Yes.</p><p>PatternFast generates patterns in DXF-AAMA format, which can be downloaded and imported into industry-standard platforms like Optitex, Browzwear, Tukatech, and CLO3D.</p><p>Once imported, you can freely modify the patterns as needed within those tools.
                </p>
              </template>
            </Accordion>
          </div>
        </div>
        <div class="col">
          <div class="info__block">
            <h2>Platform Features</h2>
            <Accordion>
              <template #question>
                <p>Can I collaborate with my team in real time?</p>
              </template>
              <template #answer>
                <p>
                  Yes.</p><p>PatternFast includes built-in team collaboration features, enabling multiple users to work on designs, review details, and manage workflows simultaneously.
                </p>
              </template>
            </Accordion>
            <Accordion>
              <template #question>
                <p>Can I export tech packs and patterns?</p>
              </template>
              <template #answer>
                <p>
                  Yes.</p><p>PatternFast generates production-ready tech packs that include measurements, fabric requirements, design details, and specifications.</p><p>You can also export your patterns in industry-standard formats.
                </p>
              </template>
            </Accordion>
            <Accordion>
              <template #question>
                <p>What if my fit chart is custom?</p>
              </template>
              <template #answer>
                <p>
                  You can use our standard fit charts (industry standard) or upload your own.</p><p>PatternFast grades patterns to any custom fit chart, ensuring your designs fit your customer base precisely.</p><p>We are size and height inclusive, fitting athletic to curvy and plus size bodies (sizes 00 - 36W), as well as petite to tall heights.
                </p>
              </template>
            </Accordion>
            <Accordion>
              <template #question>
                <p>How do I generate a tech pack for my design?</p>
              </template>
              <template #answer>
                <p>
                  Go to the Tech Pack section on the Designer page, fill out the form, and download your tech pack in Excel format, which will include all of the specked POMs and other pertinent information.</p><p>You can then import this file into any system for further development.
                </p>
              </template>
            </Accordion>
            <Accordion>
              <template #question>
                <p>Can I save and organize my designs?</p>
              </template>
              <template #answer>
                <p>
                  All the designs are organized in folders (collections).</p><p>Feel free to organize these folders however you like. You can use seasons, client names, or sub-brands for naming folders.
                </p>
              </template>
            </Accordion>
            <Accordion>
              <template #question>
                <p>Can I collaborate with other team members on a single design?</p>
              </template>
              <template #answer>
                <p>
                  All the designs that you create in a team account are available to the entire team.</p><p>All the collections created by your team will be accessible to you.
                </p>
              </template>
            </Accordion>
            <Accordion>
              <template #question>
                <p>How do I customize fit charts?</p>
              </template>
              <template #answer>
                <p>
                  In the Fit Charts section, go to the Custom tab and start creating your own Fit Chart.</p><p>You can also go to the Standard tab and customize any of the standard fit charts to your needs.
                </p>
              </template>
            </Accordion>
            <Accordion>
              <template #question>
                <p>What garment types are supported?</p>
              </template>
              <template #answer>
                <p>
                  PatternFast currently supports a wide range of apparel, including: Dresses, Skirts, Tops, T-shirts, Shirts, Pants, Jumpsuits, and Sweatshirts, with Lingerie and Swimwear coming soon.</p><p>New styles and garment categories are added regularly based on user demand.
                </p>
              </template>
            </Accordion>
            <Accordion>
              <template #question>
                <p>How do I apply different fabrics to my design?</p>
              </template>
              <template #answer>
                <p>
                  During the design process, you can filter design elements by fabric and build the product based on woven or knit fabric.</p><p>You can also use the Adjust page to change the Fabric Stretch Factor, as well as add Shrinkage and compression.
                </p>
              </template>
            </Accordion>
            <Accordion>
              <template #question>
                <p>What’s the difference between MTM and standard sizing on PatternFast?</p>
              </template>
              <template #answer>
                <p>
                  MTM (Made-to-Measure) allows you to input precise individual measurements.</p><p>Standard sizing uses predefined size charts (e.g., S, M, L or US/UK/EU sizes).</p><p>PatternFast supports both workflows, and you can switch between them in a project.
                </p>
              </template>
            </Accordion>
          </div>
        </div>
      </div>
    </div>
    <SeeInAction />
    <TheFooter />
  </div>
</template>

<script>
import Accordion from '@/components/Atoms/Accordion'
import SeeInAction from '@/components/Home/SeeInAction.vue'
import TheFooter from '@/components/Home/TheFooter.vue'
import TheHeader from '@/components/Home/TheHeader.vue'
export default {
  components: {
    Accordion,
    SeeInAction,
    TheFooter,
    TheHeader,
  },
}
</script>

<style lang="scss">
.faq {
  .container {
    width: 100%;
    max-width: 1440px;
    margin: 0 auto;
    position: relative;
    box-sizing: border-box;
    flex-direction: column;
    padding: 72px 80px 154px 80px;
  }
  .tag {
    margin-top: 60px;
    color: var(--Text-Text_Secondary);
    font-family: Unbounded;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: -0.18px;
    margin-bottom: 10px;
    line-height: normal;
  }
  h1 {
    color: var(--Text-Text_Primary);
    font-family: Unbounded;
    font-size: 45px;
    font-weight: 600;
    letter-spacing: -1.5px;
    margin-top: 0;
    margin-bottom: 60px;
    line-height: normal;
  }
  h2 {
    color: var(--Text-Text_Primary);
    font-family: Unbounded;
    font-size: 25px;
    font-weight: 600;
    letter-spacing: -0.9px;
    line-height: normal;
  }
  .info {
    display: flex;
    gap: 40px;
    &__block {
      display: flex;
      flex-direction: column;
      gap: 25px;
    }
    .col {
      width: 100%;
    }
  }
}
@media (max-width: 768px) {
  .faq {
    .tag {
      margin-top: 40px;
    }
    h1 {
      font-size: 30px;
      letter-spacing: -0.9px;
      margin-bottom: 40px;
    }
    h2 {
      font-size: 24px;
      letter-spacing: -0.72px;
    }
    .info {
      display: block;
      &__block {
        gap: 24px;
      }
      .col {
        width: 100%;
        &:nth-child(2) {
          margin-top: 40px;
        }
      }
    }
  }
}
@media (max-width: 480px) {
  .faq .container {
    padding: 82px 20px 10px 20px;
  }
}
</style>
